import { useState, useEffect } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, CreditCard, ChevronLeft } from 'lucide-react';

import { ordersApi, cmsApi, settingsApi } from '../api/requests';

export default function PayPalCheckout({ product, onSuccess, onError }) {
  const [settings, setSettings] = useState(null);
  const [cms, setCms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: form, 2: payment
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    street: '',
    postalCode: '',
    apartment: '',
    notes: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, cmsRes] = await Promise.all([
          settingsApi.getPublic(),
          cmsApi.getSection('checkout')
        ]);
        setSettings(settingsRes.data);
        setCms(cmsRes.data);
      } catch (err) {
        console.error('Failed to fetch checkout data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const validateForm = () => {
    if (!customerInfo.name.trim()) return 'Please enter your name';
    if (!customerInfo.email.trim() || !customerInfo.email.includes('@')) return 'Please enter a valid email';
    if (!customerInfo.country.trim()) return 'Please enter your country';
    if (!customerInfo.city.trim()) return 'Please enter your city';
    if (!customerInfo.street.trim()) return 'Please enter your street address';
    if (!customerInfo.postalCode.trim()) return 'Please enter your postal code';
    return null;
  };

  const handleProceedToPayment = (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setStep(2);
  };

  const createOrder = (data, actions) => {
    return actions.order.create({
      purchase_units: [
        {
          description: product.title,
          amount: {
            currency_code: settings?.paypalCurrency || 'EUR',
            value: product.priceEUR.toFixed(2),
          },
        },
      ],
    });
  };

  const onApprove = async (data, actions) => {
    try {
      const details = await actions.order.capture();
      
      // Save order — backend verifies PayPal payment and uses DB price
      const orderResp = await ordersApi.create({
        productId: product.id,
        paypalOrderId: details.id,
        customerEmail: customerInfo.email,
        customerName: customerInfo.name,
        customerPhone: customerInfo.phone || null,
        shippingCountry: customerInfo.country,
        shippingCity: customerInfo.city,
        shippingStreet: customerInfo.street,
        shippingPostalCode: customerInfo.postalCode,
        shippingApartment: customerInfo.apartment || null,
        notes: customerInfo.notes || null,
        currency: settings?.paypalCurrency || 'EUR',
      });

      if (onSuccess) {
        onSuccess(details);
      }
      
      navigate('/checkout/success', { 
        state: { 
          orderDetails: { id: orderResp.data.id, ...details },
          product,
          customerInfo 
        } 
      });
    } catch (err) {
      console.error('Order processing error:', err);
      setError('Failed to process order. Please contact support.');
      if (onError) {
        onError(err);
      }
    }
  };

  const handleError = (err) => {
    console.error('PayPal error:', err);
    setError('Payment failed. Please try again.');
    if (onError) {
      onError(err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
      </div>
    );
  }

  if (!settings?.paypalClientId) {
  return (
    <div data-testid="paypal-not-configured" className="p-6 bg-zinc-900/50 border border-white/10 rounded-xl">
      <div className="flex items-start space-x-3">
        <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="text-white font-medium mb-1">Payment Not Available</h4>
          <p className="text-zinc-400 text-sm">
            PayPal is not configured yet.
          </p>
          <button
            type="button"
            disabled
            className="mt-4 w-full py-3 bg-white/5 border border-white/10 text-zinc-500 rounded-xl cursor-not-allowed"
          >
            {cms?.proceedButtonText || 'Proceed to Payment'} - &euro;{product.priceEUR.toFixed(2)}
          </button>
        </div>
      </div>
    </div>
  );
}

  if (error) {
    return (
      <div data-testid="paypal-error" className="p-6 bg-red-500/10 border border-red-500/20 rounded-xl">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-white font-medium mb-1">Error</h4>
            <p className="text-zinc-400 text-sm">{error}</p>
            <button
              onClick={() => { setError(null); setStep(1); }}
              className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="paypal-checkout" className="space-y-6">
      {step === 1 ? (
        <form onSubmit={handleProceedToPayment} className="space-y-6">
          <h4 className="text-white font-medium font-['Outfit'] text-lg">{cms?.shippingTitle || 'Shipping Information'}</h4>
          
          {/* Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">{cms?.nameLabel || 'Full Name'} *</label>
              <input
                type="text"
                required
                value={customerInfo.name}
                onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                data-testid="checkout-name"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-colors"
                placeholder="Your full name"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">{cms?.emailLabel || 'Email'} *</label>
              <input
                type="email"
                required
                value={customerInfo.email}
                onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                data-testid="checkout-email"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-colors"
                placeholder="your@email.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-2">{cms?.phoneLabel || 'Phone (optional)'}</label>
            <input
              type="tel"
              value={customerInfo.phone}
              onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
              data-testid="checkout-phone"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-colors"
              placeholder="+49 123 456 7890"
            />
          </div>

          {/* Shipping Address */}
          <div className="pt-4 border-t border-white/10">
            <h5 className="text-white font-medium mb-4">{cms?.addressTitle || 'Shipping Address'}</h5>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-2">{cms?.countryLabel || 'Country'} *</label>
                <input
                  type="text"
                  required
                  value={customerInfo.country}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, country: e.target.value })}
                  data-testid="checkout-country"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-colors"
                  placeholder="Germany"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-2">{cms?.postalLabel || 'Postal Code'} *</label>
                <input
                  type="text"
                  required
                  value={customerInfo.postalCode}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, postalCode: e.target.value })}
                  data-testid="checkout-postal"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-colors"
                  placeholder="10115"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm text-zinc-400 mb-2">{cms?.cityLabel || 'City'} *</label>
              <input
                type="text"
                required
                value={customerInfo.city}
                onChange={(e) => setCustomerInfo({ ...customerInfo, city: e.target.value })}
                data-testid="checkout-city"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-colors"
                placeholder="Berlin"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="md:col-span-2">
                <label className="block text-sm text-zinc-400 mb-2">{cms?.streetLabel || 'Street Address'} *</label>
                <input
                  type="text"
                  required
                  value={customerInfo.street}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, street: e.target.value })}
                  data-testid="checkout-street"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-colors"
                  placeholder="Hauptstraße 123"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-2">{cms?.apartmentLabel || 'Apt/Suite'}</label>
                <input
                  type="text"
                  value={customerInfo.apartment}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, apartment: e.target.value })}
                  data-testid="checkout-apartment"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-colors"
                  placeholder="4B"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm text-zinc-400 mb-2">{cms?.notesLabel || 'Order Notes (optional)'}</label>
            <textarea
              value={customerInfo.notes}
              onChange={(e) => setCustomerInfo({ ...customerInfo, notes: e.target.value })}
              data-testid="checkout-notes"
              rows={2}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-colors resize-none"
              placeholder="Any special instructions..."
            />
          </div>

          <button
            type="submit"
            data-testid="proceed-to-payment-btn"
            className="w-full py-4 bg-indigo-500 hover:bg-indigo-600 text-white font-medium rounded-xl transition-colors"
          >
            {cms?.proceedButtonText || 'Proceed to Payment'} - &euro;{product.priceEUR.toFixed(2)}
          </button>
        </form>
      ) : (
        <>
          {/* Order Summary */}
          <div className="p-4 bg-white/5 border border-white/10 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm text-zinc-400">{cms?.shippingToLabel || 'Shipping to'}</p>
                <p className="text-white font-medium">{customerInfo.name}</p>
                <p className="text-zinc-500 text-sm">{customerInfo.email}</p>
              </div>
              <button
                onClick={() => setStep(1)}
                className="flex items-center space-x-1 text-sm text-indigo-400 hover:text-indigo-300"
              >
                <ChevronLeft className="w-4 h-4" />
                <span>{cms?.editButtonText || 'Edit'}</span>
              </button>
            </div>
            <div className="pt-3 border-t border-white/10">
              <p className="text-zinc-500 text-sm">
                {customerInfo.street}{customerInfo.apartment ? `, ${customerInfo.apartment}` : ''}
              </p>
              <p className="text-zinc-500 text-sm">
                {customerInfo.postalCode} {customerInfo.city}, {customerInfo.country}
              </p>
            </div>
          </div>

          <PayPalScriptProvider
            options={{
              clientId: settings.paypalClientId,
              currency: settings.paypalCurrency || 'EUR',
            }}
          >
            <PayPalButtons
              style={{
                layout: 'vertical',
                color: 'blue',
                shape: 'rect',
                label: 'pay',
              }}
              forceReRender={[settings.paypalClientId, settings.paypalCurrency || 'EUR', product.priceEUR]}
              createOrder={createOrder}
              onApprove={onApprove}
              onError={handleError}
              onCancel={() => navigate('/checkout/cancel')}
            />
          </PayPalScriptProvider>
        </>
      )}
    </div>
  );
}
